export interface User {
  id: number
  username: string
  stakeholder_types: StakeholderType[]
  user_type: UserType // ✅ NEW: Individual position
  email: string
  first_name: string
  last_name: string
  phone_number?: string
  logo_url?: string | null
  profile_image_url?: string | null
  has_completed_driver_onboarding: boolean
  has_completed_contractor_onboarding: boolean
  company?: Company | null
  eid?: string | null
  current_profile: UserProfile
  promo_code?: string
  stripe_customer_id?: string // Stripe customer ID for billing
}

export interface UserProfile {
  id: number
  stakeholder_type: StakeholderType
  is_current?: boolean
  created_at?: string
}

export interface ProfileSwitchResponse {
  message: string
  current_profile: UserProfile
}

export interface CreateProfileRequest {
  stakeholder_type: StakeholderType
}

export interface CreateProfileResponse {
  message: string
  profile: UserProfile
}

export interface PreUser {
  id: number
  email: string
  name?: string
  phone_number?: string
  stakeholder_types: StakeholderType[]
  user_type: UserType // ✅ NEW: Individual position
}

// NEW: User Types enum for individual positions
export enum UserType {
  DRIVER = 'DRIVER',
  DISPATCHER = 'DISPATCHER',
  PROJECT_MANAGER = 'PROJECT_MANAGER',
  SITE_SUPERVISOR = 'SITE_SUPERVISOR',
  ADMINISTRATOR = 'ADMINISTRATOR',
  OWNER = 'OWNER',
}

// UPDATED: Stakeholder Types enum now only contains company/team roles
export enum StakeholderType {
  BROKER = 'BROKER',
  SUB_HAULER = 'SUB_HAULER',
  CONTRACTOR = 'CONTRACTOR',
}

export interface ContactObject {
  id: number
  email: string
  type: 'user' | 'preuser'
  name?: string
  phone_number?: string
  stakeholder_types: StakeholderType[]
  user_type?: UserType // ✅ NEW: Individual position
  user_id?: number
  preuser_id?: number
  company_name?: string // Company name for display in dropdowns
  company_address?: string
  company_street_address?: string
  company_city?: string
  company_state?: string
  company_zip_code?: string
}

export interface Contact {
  id: number
  added_at: string
  owner_id: number
  is_external: boolean
  contact_object: ContactObject
}

export interface ContactCreate {
  email: string
  name?: string
  phone_number?: string
  stakeholder_types: StakeholderType[]
  user_type?: UserType // ✅ NEW: Individual position
  user_id?: number
  preuser_id?: number
}

export interface ContactUpdate {
  email?: string
  name?: string
  stakeholder_types?: StakeholderType[]
  user_type?: UserType // ✅ NEW: Individual position
  user_id?: number
  preuser_id?: number
}

export interface Truck {
  id: number
  truck_number: string
  truck_type: string
  license_number: string
  yard_address: string
  related_user: number
  created_date: string
  updated_date: string
}

export interface Company {
  id: number
  name: string
  address: string
  street_address?: string
  city?: string
  state?: string
  zip_code?: string // Supports zip+4: 12345-6789
  yard_address?: string
  yard_city?: string
  yard_state?: string
  yard_zip_code?: string // Supports zip+4: 12345-6789
  logo_url?: string | null
  created_date: string
  updated_date: string
}

export interface TruckCreate {
  truck_number: string
  truck_type: string
  license_number: string
  yard_address: string
}

export interface TruckUpdate extends Partial<TruckCreate> {}

export interface CompanyCreate {
  name: string
  address: string
  street_address?: string
  city?: string
  state?: string
  zip_code?: string // Supports zip+4: 12345-6789
  yard_address?: string
  yard_city?: string
  yard_state?: string
  yard_zip_code?: string // Supports zip+4: 12345-6789
  logo?: File
}

export interface UserUpdate {
  first_name?: string
  last_name?: string
  phone_number?: string
  email?: string
}

// UPDATED: Team invite now uses user_type and optional stakeholder_type
export interface TeamInvite {
  id: number
  email: string
  user_type: UserType
  stakeholder_type?: StakeholderType | null
  promo_code: string
  accepted: boolean
  created_date: string
  accepted_at?: string | null
  note?: string | null
  company?: {
    id: number
    name: string
    address: string
  } | null
}

export interface TeamInviteCreate {
  email: string
  user_type: UserType
  stakeholder_type?: StakeholderType // Optional stakeholder type (defaults to inviter's current profile)
  note?: string // Optional note for the team invitation (max 250 characters)
}

export interface TeamInviteResponse {
  id: number
  email: string
  user_type: UserType
  stakeholder_type?: StakeholderType | null
  promo_code: string
  created_date: string
  message: string
}

export interface TeamInviteResendResponse {
  message: string
  email: string
}

// External invite types (for inviting people to join DayDaCo, not your company)
export interface ExternalInvite {
  id: number
  email: string
  stakeholder_type?: StakeholderType | null
  accepted: boolean
  created_date: string
  accepted_at?: string | null
  note?: string | null
  invited_by?: {
    id: number
    email: string
    first_name: string
    last_name: string
  } | null
}

export interface ExternalInviteCreate {
  email: string
  stakeholder_type?: StakeholderType // Optional stakeholder type (defaults to inviter's current profile)
  note?: string // Optional note for the external invitation (max 250 characters)
}

export interface ExternalInviteResponse {
  id: number
  email: string
  stakeholder_type?: StakeholderType | null
  promo_code: string
  created_date: string
  message: string
}

export interface ExternalInviteResendResponse {
  message: string
  email: string
}

// Billing Contact Management Types
export interface BillingContact {
  id: number
  first_name: string
  last_name: string
  email: string
  user_type: UserType
}

export interface BillingContactResponse {
  message: string
  billing_contact: BillingContact | null
}

export interface SetBillingContactRequest {
  user_id: number
}

// Empty object - backend auto-uses existing payment method attached to customer
export type CompanyBillingSetupRequest = Record<string, never>

export interface CompanyBillingSetupResponse {
  message: string
  subscription: {
    id: string
    status: string
    current_period_start: number
    current_period_end: number
  }
}
